import { useId } from 'react';
import { Button } from '../Button/Button';
import { Select } from '../Select/Select';
import styles from './Pagination.module.css';

/**
 * Design-system §6's pagination (*Data table (F32) → Pagination*): numbered pages with a total,
 * **`1–25 of 340` plus Previous / Next, footer-right**, over FS-10's offset paging (1-based `page`,
 * `size`, an exact `totalItems` and `totalPages` in every list envelope).
 *
 * Built in `src/ui` by F26-b (F26-b FR-U1; F26-b WI-1.3 Decision 21) because two features page lists — the
 * Standards book, the account panel, and F1-b's account list next — and one feature may not import
 * another's component. `InboxPager` stays the email feature's own.
 *
 * **The page-size select §6 also names is optional** (F11-b WI-1.2 plan Decision 11, built here for the
 * Leads screen and F1-b's account list alike): given both `pageSizes` and `onPageSize`, the pager
 * carries a compact *Rows per page* select and renders whenever there is anything to page
 * (`totalItems > 0`), so a one-page list still shows its range and the size control. Without them it
 * behaves as before: the Standards book and the account panel are unchanged.
 *
 * It takes display props, not a wire type, because `src/ui` imports nothing from `src/lib`.
 *
 * - **Without a page-size select, nothing renders for one page or none** — a pager with nowhere to go
 *   is noise. With one, nothing renders only for an empty set.
 * - **While a page change loads, both controls are unavailable but keep focus**: `Button` renders
 *   `aria-disabled`, never the native attribute, so a keyboard user's focus stays on the control they
 *   pressed (WCAG 2.4.3).
 * - **Each control reports an offset** (`-1` / `+1`), not a page number, so a press cannot count from
 *   a render one press behind.
 */
export interface PaginationProps {
    /** The 1-based page on screen. */
    readonly pageNumber: number;
    /** The size asked for — `page.size`, not the number of rows on this page. */
    readonly pageSize: number;
    readonly totalItems: number;
    readonly totalPages: number;
    /** Rows actually on this page, which is the real upper bound of the range on the last page. */
    readonly itemCount: number;
    readonly onStep: (offset: -1 | 1) => void;
    /** A page change is loading. */
    readonly busy?: boolean;
    /** Names the `<nav>` — "Standards book pages". */
    readonly label: string;
    /**
     * The page sizes the *Rows per page* select offers, in order. The select renders only when this
     * and `onPageSize` are both given; the current size is `pageSize`.
     */
    readonly pageSizes?: readonly number[];
    /** A size chosen in the *Rows per page* select. */
    readonly onPageSize?: (size: number) => void;
    /**
     * The pager's hook; its parts take `<id>-range`, `<id>-previous`, `<id>-next` and, with the
     * page-size select, `<id>-size`.
     */
    readonly 'data-testid'?: string;
}

/** `1–25 of 340` — an en dash, per §6. */
export function paginationRange(
    pageNumber: number,
    pageSize: number,
    itemCount: number,
    totalItems: number,
): string {
    const first = (pageNumber - 1) * pageSize + 1;
    const last = first + Math.max(itemCount, 1) - 1;
    return `${first}–${last} of ${totalItems}`;
}

export function Pagination({
    pageNumber,
    pageSize,
    totalItems,
    totalPages,
    itemCount,
    onStep,
    busy = false,
    label,
    pageSizes,
    onPageSize,
    'data-testid': testId = 'pagination',
}: PaginationProps) {
    const sizeId = useId();
    const sizes = pageSizes !== undefined && onPageSize !== undefined ? pageSizes : undefined;
    if (sizes === undefined ? totalPages <= 1 : totalItems <= 0) {
        return null;
    }
    return (
        <nav aria-label={label} className={styles.pagination} data-testid={testId}>
            <span className={styles.range} data-testid={`${testId}-range`}>
                {paginationRange(pageNumber, pageSize, itemCount, totalItems)}
            </span>
            <Button
                data-testid={`${testId}-previous`}
                disabled={busy || pageNumber <= 1}
                onClick={() => onStep(-1)}
                size="sm"
                variant="secondary"
            >
                Previous
            </Button>
            <Button
                data-testid={`${testId}-next`}
                disabled={busy || pageNumber >= totalPages}
                onClick={() => onStep(1)}
                size="sm"
                variant="secondary"
            >
                Next
            </Button>
            {sizes === undefined || onPageSize === undefined ? null : (
                // A label beside the control rather than a Field: a footer row has no room for
                // Field's reserved message line, and a page size has nothing to validate
                // (`UsersPager`'s pattern).
                <span className={styles.pageSize}>
                    <label className={styles.range} htmlFor={sizeId}>
                        Rows per page
                    </label>
                    <Select
                        data-testid={`${testId}-size`}
                        id={sizeId}
                        onChange={(event) => onPageSize(Number(event.target.value))}
                        options={sizes.map((size) => ({ value: String(size), label: String(size) }))}
                        size="compact"
                        value={String(pageSize)}
                    />
                </span>
            )}
        </nav>
    );
}
